import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Search, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CollectionTemplatesQuery } from '@/graphql/queries/collectionTemplatesQuery.gql.generated'
import { CollectionTemplateScope } from '@/graphql/schema'
import {
  useCreateDocumentCollectionFromTemplateMutation,
  useDeleteCollectionTemplateMutation,
} from '@/graphql/mutations/collectionTemplateMutations.gql.generated'
import {
  CollectionTemplatesDocument,
  useCollectionTemplatesQuery,
} from '@/graphql/queries/collectionTemplatesQuery.gql.generated'
import { DocumentCollectionsDocument } from '@/graphql/queries/documentCollectionsQuery.gql.generated'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'

type Template = CollectionTemplatesQuery['collectionTemplates'][number]

export function CollectionTemplateLibrary({
  open,
  onCreated,
}: {
  open: boolean
  onCreated: () => void
}) {
  const model = useTemplateLibraryModel({ open, onCreated })
  const { t } = useTranslation('documentCollections')

  if (model.loading) {
    return (
      <p className="px-4 text-sm text-muted-foreground">
        {t('loadingTemplates')}
      </p>
    )
  }
  if (model.error) {
    return (
      <p role="alert" className="px-4 text-sm text-destructive">
        {model.error}
      </p>
    )
  }

  return <TemplateLibraryContent model={model} />
}

function TemplateLibraryContent({
  model,
}: {
  model: ReturnType<typeof useTemplateLibraryModel>
}) {
  const { t } = useTranslation('documentCollections')

  return (
    <div className="space-y-4 px-4 pb-4">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-muted-foreground" />
        <Input
          value={model.search}
          className="pl-9"
          placeholder={t('searchTemplates')}
          onChange={(event) => model.setSearch(event.target.value)}
        />
      </label>
      <TemplateGroup
        title={t('predefinedTemplates')}
        templates={model.systemTemplates}
        selectedId={model.selected?.id}
        deleting={model.deleting}
        onDelete={() => undefined}
        onSelect={model.select}
      />
      <TemplateGroup
        title={t('workspaceTemplates')}
        templates={model.workspaceTemplates}
        selectedId={model.selected?.id}
        deleting={model.deleting}
        onDelete={model.deleteTemplate}
        onSelect={model.select}
      />
      {model.selected ? <SelectedTemplateForm model={model} /> : null}
    </div>
  )
}

function SelectedTemplateForm({
  model,
}: {
  model: ReturnType<typeof useTemplateLibraryModel>
}) {
  const { t } = useTranslation('documentCollections')
  const selected = model.selected
  if (!selected) return null

  return (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
      <Label htmlFor="template-collection-name">{t('newCollectionName')}</Label>
      <Input
        id="template-collection-name"
        value={model.name}
        onChange={(event) => model.setName(event.target.value)}
      />
      <div>
        <p className="text-sm font-medium">{t('fieldPreview')}</p>
        <ol className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
          {selected.fields.map((field, index) => (
            <li key={`${field.name}:${index}`}>
              {index + 1}. {field.name}
            </li>
          ))}
        </ol>
      </div>
      {model.submitError ? (
        <p role="alert" className="text-sm text-destructive">
          {model.submitError}
        </p>
      ) : null}
      <Button
        type="button"
        disabled={!model.name.trim() || model.creating}
        onClick={() => void model.create()}
      >
        {model.creating ? t('creatingFromTemplate') : t('createCollection')}
      </Button>
    </div>
  )
}

function useTemplateLibraryModel({
  open,
  onCreated,
}: {
  open: boolean
  onCreated: () => void
}) {
  const { t } = useTranslation('documentCollections')
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string>()
  const [name, setName] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const query = useCollectionTemplatesQuery({ skip: !open })
  const [createMutation, createState] =
    useCreateDocumentCollectionFromTemplateMutation({
      refetchQueries: [{ query: DocumentCollectionsDocument }],
    })
  const [deleteMutation, deleteState] = useDeleteCollectionTemplateMutation({
    refetchQueries: [{ query: CollectionTemplatesDocument }],
  })
  const templates = query.data?.collectionTemplates ?? []
  const filtered = filterTemplates(templates, search)
  const selected = templates.find((template) => template.id === selectedId)
  const select = (template: Template) => {
    setSelectedId(template.id)
    setName(template.name)
  }
  const deleteTemplate = async (id: string) => {
    await deleteMutation({ variables: { id } })
    if (selectedId === id) setSelectedId(undefined)
  }
  const create = async () => {
    if (!selected || !name.trim()) return
    try {
      setSubmitError(null)
      const result = await createMutation({
        variables: { input: { templateId: selected.id, name: name.trim() } },
      })
      const collection = result.data?.createDocumentCollectionFromTemplate
      if (!collection) throw new Error(t('addCollectionError'))
      onCreated()
      await navigate({
        to: '/document-collection/$id',
        params: { id: collection.id },
      })
    } catch (caught) {
      setSubmitError(
        caught instanceof Error ? caught.message : t('addCollectionError'),
      )
    }
  }

  return {
    create,
    creating: createState.loading,
    deleteTemplate,
    deleting: deleteState.loading,
    error: query.error?.message,
    loading: query.loading && !query.data,
    name,
    search,
    select,
    selected,
    setName,
    setSearch,
    submitError,
    systemTemplates: filtered.filter(isSystemTemplate),
    workspaceTemplates: filtered.filter(isWorkspaceTemplate),
  }
}

function filterTemplates(templates: Array<Template>, search: string) {
  const term = search.trim().toLocaleLowerCase()

  return templates.filter(
    (template) =>
      !term ||
      template.name.toLocaleLowerCase().includes(term) ||
      template.description.toLocaleLowerCase().includes(term) ||
      template.fields.some((field) =>
        field.name.toLocaleLowerCase().includes(term),
      ),
  )
}

function isSystemTemplate(template: Template) {
  return template.scope === CollectionTemplateScope.System
}

function isWorkspaceTemplate(template: Template) {
  return template.scope === CollectionTemplateScope.Workspace
}

function TemplateGroup({
  deleting,
  onDelete,
  onSelect,
  selectedId,
  templates,
  title,
}: {
  deleting: boolean
  onDelete: (id: string) => void | Promise<void>
  onSelect: (template: Template) => void
  selectedId?: string
  templates: Array<Template>
  title: string
}) {
  if (templates.length === 0) return null

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="grid gap-2 sm:grid-cols-2">
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            deleting={deleting}
            selected={selectedId === template.id}
            template={template}
            onDelete={onDelete}
            onSelect={onSelect}
          />
        ))}
      </div>
    </section>
  )
}

function TemplateCard({
  deleting,
  onDelete,
  onSelect,
  selected,
  template,
}: {
  deleting: boolean
  onDelete: (id: string) => void | Promise<void>
  onSelect: (template: Template) => void
  selected: boolean
  template: Template
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${selected ? 'border-primary bg-primary/5' : ''}`}
    >
      <button
        type="button"
        className="w-full text-left"
        onClick={() => onSelect(template)}
      >
        <span className="font-medium">{template.name}</span>
        <span className="mt-1 block text-xs text-muted-foreground">
          {template.description}
        </span>
      </button>
      {template.scope === CollectionTemplateScope.Workspace ? (
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          className="mt-2"
          disabled={deleting}
          aria-label={`Delete ${template.name}`}
          onClick={() => void onDelete(template.id)}
        >
          <Trash2 className="size-4" />
        </Button>
      ) : null}
    </div>
  )
}
